import { useEffect, useRef } from "react";

/**
 * Pull-to-refresh hook for mobile.
 * Usage: usePullToRefresh(onRefresh) — calls onRefresh() when user pulls down from top.
 */
export default function usePullToRefresh(onRefresh) {
  const startY = useRef(0);
  const pulling = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined" || window.innerWidth > 768) return;

    let indicator = null;

    const onTouchStart = (e) => {
      if (window.scrollY === 0) {
        startY.current = e.touches[0].clientY;
        pulling.current = true;
      }
    };

    const onTouchMove = (e) => {
      if (!pulling.current) return;
      const diff = e.touches[0].clientY - startY.current;
      if (diff > 60 && window.scrollY === 0) {
        if (!indicator) {
          indicator = document.createElement("div");
          indicator.id = "dm-ptr";
          indicator.style.cssText = "position:fixed;top:0;left:0;right:0;height:3px;background:var(--r);z-index:9999;animation:dmPtrPulse 1s infinite;";
          document.body.appendChild(indicator);
        }
      }
    };

    const onTouchEnd = () => {
      if (!pulling.current) return;
      pulling.current = false;
      if (indicator) {
        indicator.remove();
        indicator = null;
        if (onRefresh) onRefresh();
      }
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: true });
    document.addEventListener("touchend", onTouchEnd, { passive: true });

    // Add pulse animation
    const style = document.createElement("style");
    style.textContent = "@keyframes dmPtrPulse { 0%,100% { opacity:.4 } 50% { opacity:1 } }";
    document.head.appendChild(style);

    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      style.remove();
    };
  }, [onRefresh]);
}
